import { JSX, useEffect, useId, useState } from "react";
import { SearchType } from "../../types";
import styles from "./SearchComponent.module.css";
import { currencyFormatter } from "../../helpers";
import { globals, images, onboarding } from "../../styles";
import {
  artist_Icon,
  chevron_Icon,
  groups_Icon,
  search_Icon,
} from "../../assets/icons";
import {
  isSearchResultArtist,
  isSearchResultGroup,
  SearchResult,
} from "../../api/types";
import { useArtistSearch, useGroupSearch } from "../../hooks/search";
import { useActiveDraft } from "../../hooks/drafts";

function SearchComponent(props: {
  searchType: SearchType;
  handleResultClicked: (res: SearchResult) => void;
  clearOnClick?: boolean;
  disableSearch?: boolean;
  validationFunction?: (res: SearchResult) => boolean;
  searchFieldRef?: React.RefObject<HTMLInputElement | null>;
  autoSearch?: boolean;
  showCost?: boolean;
  resultsClassName?: string;
  resultClassName?: string;
}) {
  const {
    handleResultClicked,
    clearOnClick,
    disableSearch,
    searchType,
    validationFunction,
    searchFieldRef,
    autoSearch = true,
    showCost = false,
    resultsClassName,
    resultClassName,
  } = props;

  const inputId = useId();
  const { data: activeDraft } = useActiveDraft();
  const {
    mutate: searchArtists,
    data: artistSearchResults,
    isPending: isArtistSearchPending,
    reset: resetArtistResults,
  } = useArtistSearch();
  const draftId = !activeDraft
    ? undefined
    : activeDraft.active
      ? activeDraft.draftId
      : (activeDraft.nextDraft?.draftId ?? undefined);
  const {
    mutate: searchGroups,
    data: groupSearchResults,
    isPending: isGroupSearchPending,
    reset: resetGroupResults,
  } = useGroupSearch(draftId);

  const isPending =
    searchType === "artist" ? isArtistSearchPending : isGroupSearchPending;
  const searchResults =
    searchType === "artist" ? artistSearchResults : groupSearchResults?.groups;
  const resetResults =
    searchType === "artist" ? resetArtistResults : resetGroupResults;
  const [searchQuery, setSearchQuery] = useState<string>("");

  const [offset, setOffset] = useState(0);
  const limit = 9;

  const handleClear = () => {
    setSearchQuery("");
    resetResults();
    setOffset(0);
  };

  const handleArtistSearch = (searchOffset: number) => {
    const q = searchQuery.trim();
    searchArtists({ q, draftId, offset: searchOffset, limit });
  };

  const handleGroupSearch = (searchOffset: number) => {
    const q = searchQuery.trim();
    if (draftId) {
      searchGroups({ q, offset: searchOffset, limit });
    }
  };

  const handleSearch = (searchOffset: number) => {
    if (disableSearch) return;
    switch (searchType) {
      case "artist":
        handleArtistSearch(searchOffset);
        break;
      case "group":
        handleGroupSearch(searchOffset);
        break;
      default:
        break;
    }
  };

  const handleNext = () => {
    const newOffset = offset + limit;
    setOffset(newOffset);
    handleSearch(newOffset);
  };

  const handlePrev = () => {
    const newOffset = Math.max(0, offset - limit);
    setOffset(newOffset);
    handleSearch(newOffset);
  };

  function printResult(res: SearchResult): string | JSX.Element {
    if (isSearchResultArtist(res) && res.currentPrice) {
      let costClass = "";
      if (res.previousPrice && res.currentPrice - res.previousPrice > 0) {
        costClass = "green";
      } else if (
        res.previousPrice &&
        res.currentPrice - res.previousPrice < 0
      ) {
        costClass = "red";
      }
      return (
        <>
          {res.artistName} (
          <em className={styles[costClass]}>
            {currencyFormatter(res.currentPrice)}
          </em>
          )
        </>
      );
    }
    return isSearchResultGroup(res) ? res.groupName : res.artistName;
  }

  useEffect(() => {
    if (autoSearch) handleSearch(0);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className={styles["search-component"]}>
      <div className={styles["search-bar"]}>
        <img src={search_Icon} alt="" className={images["search-icon"]} />
        <input
          type="text"
          id={inputId}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder={
            searchType === "artist" ? "Search for Artists!" : "Search by Name"
          }
          onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key === "Enter") handleSearch(offset);
          }}
          className={styles["input"]}
          disabled={disableSearch || isPending}
          ref={searchFieldRef}
        />
      </div>
      <button
        className={globals["button-wrapper"]}
        onClick={() => handleSearch(offset)}
        disabled={disableSearch || isPending}
      >
        <div>Search</div>
      </button>
      {searchResults && searchResults.length > 0 && searchType === "artist" && (
        <div className={styles["artist-results"]}>
          {searchResults.map((res) => {
            const id = isSearchResultGroup(res) ? res.groupId : res.artistId;
            const name = isSearchResultGroup(res)
              ? res.groupName
              : res.artistName;
            return (
              <button
                onClick={() => {
                  handleResultClicked(res);
                  if (clearOnClick) {
                    handleClear();
                  }
                }}
                key={id}
                className={globals["button-wrapper"]}
                disabled={validationFunction ? !validationFunction(res) : false}
              >
                <div className={styles["artist-result-card"]}>
                  <div className={onboarding["onboarding-icon-wrapper"]}>
                    <img
                      src={artist_Icon}
                      alt={name}
                      className={images["artist-icon-onboarding"]}
                    />
                  </div>
                  <span className={styles["artist-result-name"]}>{name}</span>
                  {showCost &&
                    isSearchResultArtist(res) &&
                    res.currentPrice && (
                      <span className={styles["artist-result-cost"]}>
                        {currencyFormatter(res.currentPrice)}
                      </span>
                    )}
                </div>
              </button>
            );
          })}
        </div>
      )}
      {searchResults && searchResults.length > 0 && searchType === "group" && (
        <div className={resultsClassName ?? styles["results"]}>
          {searchResults.map((res) => {
            const id = isSearchResultGroup(res) ? res.groupId : res.artistId;
            const name = isSearchResultGroup(res)
              ? res.groupName
              : res.artistName;
            return (
              <button
                onClick={() => {
                  handleResultClicked(res);
                  if (clearOnClick) {
                    handleClear();
                  }
                }}
                key={id}
                className={globals["button-wrapper"]}
                disabled={validationFunction ? !validationFunction(res) : false}
              >
                <div className={resultClassName ?? styles["result-button"]}>
                  {resultClassName && (
                    <div className={onboarding["onboarding-icon-wrapper"]}>
                      <img
                        src={groups_Icon}
                        alt={name}
                        className={images["artist-icon-onboarding"]}
                      />
                    </div>
                  )}
                  <span>{printResult(res)}</span>
                </div>
              </button>
            );
          })}
        </div>
      )}
      {searchResults && searchResults.length > 0 && (
        <div className={styles["pagination"]}>
          <button
            className={globals["button-wrapper"]}
            onClick={handlePrev}
            disabled={offset === 0 || isPending}
          >
            <div className={styles["pagination-chevron"]}>
              <img
                src={chevron_Icon}
                alt="Previous"
                className={styles["pagination-chevron--prev"]}
                style={{ width: 50, height: 50 }}
              />
            </div>
          </button>
          <button
            className={globals["button-wrapper"]}
            onClick={handleNext}
            disabled={searchResults.length < limit || isPending}
          >
            <div className={styles["pagination-chevron"]}>
              <img
                src={chevron_Icon}
                alt="Next"
                style={{ width: 50, height: 50 }}
              />
            </div>
          </button>
        </div>
      )}
    </div>
  );
}

export default SearchComponent;
